import { Button } from "@components";
import { SwitchHorizontal01, X, Zap } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cx } from "@/utils/cx";
import {
	useDamageCameraStream,
	useFullscreen,
	useLandscapeOrientation,
} from "../hooks";
import { usePhotoOnboardingStore } from "../store";
import type { AnnotatedFile, FileUploadStatusValue } from "../types";
import {
	ImageAnnotation,
	ImagePreview,
	LandscapeWrapper,
	PhotoOnboarding,
} from "./";

type Mode = "idle" | "camera" | "editing" | "done";

type DamageCameraCaptureProps = {
	onCapture: (file: AnnotatedFile) => void;
	fileName?: string;
	value?: AnnotatedFile;
	error?: boolean;
	allowDamageReport?: boolean;
	isRequired?: boolean;
	onRemove?: () => void;
	uploadStatus?: FileUploadStatusValue;
	uploadErrorMessage?: string;
	onRetry?: (file?: AnnotatedFile) => void;
};

export default function DamageCameraCapture({
	onCapture,
	fileName = "dano.png",
	value,
	error = false,
	allowDamageReport = true,
	isRequired = true,
	onRemove,
	uploadStatus,
	uploadErrorMessage,
	onRetry,
}: DamageCameraCaptureProps) {
	const { hasCompletedInitialOnboarding, completeInitialOnboarding } =
		usePhotoOnboardingStore();

	const [showInitialOnboarding, setShowInitialOnboarding] = useState(false);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const videoRef = useRef<HTMLVideoElement>(null);
	const [mode, setMode] = useState<Mode>("idle");
	const [photoBlob, setPhotoBlob] = useState<Blob | null>(null);
	const isLandscape = useLandscapeOrientation();
	const { fullscreenFallback, enterFullscreen, exitFullscreen } =
		useFullscreen(containerRef);
	const [shouldStartCamera, setShouldStartCamera] = useState(false);
	const [shouldEnterFullscreen, setShouldEnterFullscreen] = useState(false);
	const [localFile, setLocalFile] = useState<AnnotatedFile | null>(
		value ?? null
	);

	const previewFile = useMemo(
		() => localFile ?? value ?? null,
		[localFile, value]
	);

	const {
		stream,
		cameraError,
		isVideoReady,
		hasTriedStream,
		isFlashOn,
		isFlashSupported,
		currentFacingMode,
		setCameraError,
		setIsVideoReady,
		requestCameraPermission,
		startCamera,
		stopCamera,
		toggleCamera,
		toggleFlash,
		setFlash,
	} = useDamageCameraStream({ videoRef, isLandscape });

	const isBlockingOrientation = !isLandscape;
	const shouldShowEditor = mode === "editing" && photoBlob;
	const shouldShowCamera = mode === "camera" && !photoBlob;
	const [flashBeforeEditing, setFlashBeforeEditing] = useState(false);

	const handleFinalFile = useCallback(
		async (file: AnnotatedFile) => {
			const uri = file.uri ?? URL.createObjectURL(file as File);
			const finalFile: AnnotatedFile = Object.assign(file, { uri });
			setLocalFile(finalFile);
			onCapture(finalFile);
			setMode("idle");
			setPhotoBlob(null);
			await stopCamera();
			exitFullscreen();
		},
		[onCapture, stopCamera, exitFullscreen]
	);

	const takePhoto = useCallback(() => {
		const video = videoRef.current;
		if (!video) {
			return setCameraError("Câmera não inicializada.");
		}

		const rect = video.getBoundingClientRect();
		const videoRatio = video.videoWidth / video.videoHeight;
		const previewRatio = rect.width / rect.height;

		let sx = 0,
			sy = 0,
			sw = video.videoWidth,
			sh = video.videoHeight;

		if (videoRatio > previewRatio) {
			sw = video.videoHeight * previewRatio;
			sx = (video.videoWidth - sw) / 2;
		} else {
			sh = video.videoWidth / previewRatio;
			sy = (video.videoHeight - sh) / 2;
		}

		const maxWidth = 1280;
		const scale = maxWidth / sw;
		const targetWidth = maxWidth;
		const targetHeight = Math.round(sh * scale);

		const canvas = document.createElement("canvas");
		canvas.width = targetWidth;
		canvas.height = targetHeight;

		const ctx = canvas.getContext("2d");
		if (!ctx) {
			return;
		}

		ctx.drawImage(video, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);

		canvas.toBlob(
			(blob) => {
				if (!blob) {
					return;
				}

				const imgFile = Object.assign(
					new File([blob], fileName, { type: "image/jpeg" }),
					{ uri: URL.createObjectURL(blob), damages: [] }
				);

				if (!allowDamageReport) {
					handleFinalFile(imgFile);
					return;
				}

				setPhotoBlob(blob);
				setMode("editing");
			},
			"image/jpeg",
			0.7
		);
	}, [fileName, handleFinalFile, allowDamageReport, setCameraError]);

	const retake = useCallback(() => {
		setPhotoBlob(null);
		setMode("camera");
	}, []);

	const handleOpenCamera = async () => {
		// Solicita permissão da câmera ANTES de entrar em fullscreen
		const hasPermission = await requestCameraPermission();
		if (!hasPermission) {
			// Se rejeitar, não faz nada (toast será exibido pela função requestCameraPermission)
			return;
		}

		// Se aceitar, entra em fullscreen e inicia a câmera
		setMode("camera");
		setPhotoBlob(null);
		setShouldStartCamera(true);
		setShouldEnterFullscreen(true);

		await enterFullscreen();
		const started = await startCamera();
		if (started) {
			setShouldStartCamera(false);
		}
	};

	const handleClose = async () => {
		setMode("idle");
		setPhotoBlob(null);
		setShouldStartCamera(false);
		setShouldEnterFullscreen(false);
		await stopCamera();
		exitFullscreen();
	};

	useEffect(() => {
		const wantsCamera = mode === "camera" || mode === "editing";
		if (!wantsCamera) {
			return;
		}
		// landscape: inicia fullscreen e câmera se solicitado
		if (shouldEnterFullscreen) {
			enterFullscreen();
			setShouldEnterFullscreen(false);
		}

		if (shouldStartCamera && !stream) {
			startCamera().then((started) => {
				if (started) {
					setShouldStartCamera(false);
				}
			});
		}
	}, [
		enterFullscreen,
		mode,
		shouldEnterFullscreen,
		shouldStartCamera,
		startCamera,
		stream,
	]);
	// Garantir fullscreen após montagem do container
	useEffect(() => {
		if ((mode === "camera" || mode === "editing") && containerRef.current) {
			enterFullscreen();
		}
	}, [enterFullscreen, mode]);

	const handleImagePreviewClick = () => {
		// Se ainda não completou o onboarding inicial, apenas abre o onboarding.
		// O próprio onboarding, ao ser concluído, chama `handleOpenCamera`.
		if (!hasCompletedInitialOnboarding) {
			setShowInitialOnboarding(true);
			return;
		}
		// Se já completou o onboarding, vai direto para a câmera
		handleOpenCamera();
	};

	useEffect(() => {
		if (mode === "editing" && isFlashOn) {
			setFlashBeforeEditing(true);
			setFlash(false); // desliga ao entrar em avarias
		}

		if (mode === "camera" && flashBeforeEditing) {
			setFlash(true); // religa ao voltar
			setFlashBeforeEditing(false);
		}
	}, [mode, isFlashOn, flashBeforeEditing, setFlash]);

	if (mode === "idle" || mode === "done") {
		return (
			<>
				{showInitialOnboarding ? (
					<PhotoOnboarding
						onClick={() => {
							completeInitialOnboarding();
							setShowInitialOnboarding(false);
							handleOpenCamera();
						}}
					/>
				) : null}
				<ImagePreview
					className="overflow-hidden rounded-sm"
					error={error}
					icon="camera"
					isRequired={isRequired}
					label="Tirar foto"
					onClick={handleImagePreviewClick}
					onRemove={
						isRequired
							? undefined
							: () => {
									setLocalFile(null);
									if (onRemove) {
										onRemove();
									}
								}
					}
					onRetry={() => onRetry?.(localFile ?? value)}
					previewUri={previewFile?.uri}
					uploadErrorMessage={
						uploadErrorMessage ?? previewFile?.uploadErrorMessage
					}
					uploadStatus={uploadStatus ?? previewFile?.uploadStatus}
				/>
			</>
		);
	}

	return (
		<div
			className={cx(
				"fixed inset-0 z-40 bg-black",
				fullscreenFallback ? "overflow-hidden" : ""
			)}
			ref={containerRef}
		>
			{!!shouldShowEditor && photoBlob ? (
				<ImageAnnotation
					fileName={fileName}
					img={photoBlob}
					isLandscape={isLandscape}
					onExit={exitFullscreen}
					onInteraction={enterFullscreen}
					onRetake={retake}
					onSave={handleFinalFile}
				/>
			) : null}

			<video
				autoPlay
				className={cx(
					"h-full w-full object-cover",
					!shouldShowCamera && "hidden"
				)}
				muted
				onCanPlay={() => setIsVideoReady(true)}
				onLoadedMetadata={() => {
					if (videoRef.current) {
						const hasValidDimensions =
							!!videoRef.current.videoWidth && !!videoRef.current.videoHeight;
						setIsVideoReady(hasValidDimensions);
						videoRef.current.play().catch(() => setIsVideoReady(false));
					}
				}}
				onPlay={() => {
					setIsVideoReady(true);
					enterFullscreen();
				}}
				playsInline
				ref={videoRef}
			/>
			<div
				className={cx(
					"absolute inset-y-0 right-0 flex flex-col items-end justify-between bg-black/50 p-6 py-10",
					!shouldShowCamera && "hidden"
				)}
			>
				<button onClick={handleClose} type="button">
					<X className="size-10 text-bg-secondary" />
				</button>

				<button
					className="size-20 rounded-full border-4 border-white bg-white/30 shadow-2xl backdrop-blur disabled:opacity-50"
					disabled={isBlockingOrientation}
					onClick={takePhoto}
					type="button"
				>
					<div className="m-auto size-16 rounded-full bg-white" />
				</button>

				<button
					className="disabled:opacity-50"
					disabled={isBlockingOrientation}
					onClick={toggleCamera}
					type="button"
				>
					<SwitchHorizontal01 className="size-10 text-bg-secondary" />
				</button>
			</div>

			{shouldShowCamera &&
				currentFacingMode === "environment" &&
				isFlashSupported && (
					<button
						className={cx(
							"absolute top-6 left-6 size-16 rounded-full bg-black/50 p-4",
							!!isFlashOn && "opacity-100"
						)}
						disabled={isBlockingOrientation}
						onClick={toggleFlash}
						type="button"
					>
						<Zap
							className={cx(
								"size-8",
								isFlashOn ? "text-yellow-400" : "text-bg-secondary"
							)}
						/>
					</button>
				)}

			{!cameraError && hasTriedStream && !(!!stream && isVideoReady) && (
				<div
					className={cx(
						"absolute inset-0 flex items-center justify-center bg-black/70 text-white",
						!shouldShowCamera && "hidden"
					)}
				>
					<p className="text-sm">Aguardando inicialização da câmera...</p>
				</div>
			)}

			{isBlockingOrientation ? (
				<LandscapeWrapper message="Use o modo horizontal para capturar ou editar a foto." />
			) : null}

			{cameraError ? (
				<div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80 p-6 text-center text-white">
					<p className="font-semibold text-base">{cameraError}</p>
					<div className="flex gap-2">
						<Button
							onClick={async () => {
								setCameraError(null);
								// Para qualquer stream anterior antes de tentar novamente
								await stopCamera();
								// Aguarda um pouco para garantir limpeza
								await new Promise((resolve) => setTimeout(resolve, 300));
								// Tenta iniciar novamente
								await startCamera();
							}}
							type="button"
							variant="default"
						>
							Tentar novamente
						</Button>
						<Button onClick={handleClose} type="button" variant="secondary">
							Fechar
						</Button>
					</div>
				</div>
			) : null}
		</div>
	);
}
